import { getAircraft, getSeatMap, type Aircraft } from '@trippath/shared';
import { useEffect, useState } from 'react';

import { typeCodeOf } from '../../lib/reference';
import { planOf, type SeatPlan } from '../../lib/seats';

/** A leg as far as its seat map goes: its aircraft and its airline's code. */
interface Flown {
  vehicle: string | null;
  airline: string | null;
}

/**
 * The seat maps of the aircraft some legs fly, as they come: the plan a leg's seats are placed
 * on, none until its map has come or when there is none. An aircraft said by its name alone,
 * `Airbus A350`, is found among the types known, asked for once.
 */
export function useSeatPlans(legs: Flown[]): (leg: Flown) => SeatPlan | null {
  const [plans, setPlans] = useState(new Map<string, SeatPlan | null>());
  const [types, setTypes] = useState<Aircraft[]>([]);
  const keyOf = (leg: Flown) =>
    `${typeCodeOf(leg.vehicle ?? '', types) ?? ''}|${leg.airline ?? ''}`;
  const named = legs
    .map((leg) => (leg.vehicle ?? '').trim())
    .some((vehicle) => vehicle !== '' && typeCodeOf(vehicle, []) === null);
  useEffect(() => {
    if (named) getAircraft().then(setTypes, () => undefined);
  }, [named]);
  const wanted = [
    ...new Set(legs.filter((leg) => typeCodeOf(leg.vehicle ?? '', types) !== null).map(keyOf)),
  ].join(',');
  useEffect(() => {
    for (const key of wanted === '' ? [] : wanted.split(',')) {
      const [icao = '', airline = ''] = key.split('|');
      getSeatMap(icao, airline === '' ? null : airline).then(
        (map) => {
          setPlans((was) => new Map(was).set(key, planOf(map)));
        },
        // Without its map, a seat says no side.
        () => undefined,
      );
    }
  }, [wanted]);
  return (leg) => plans.get(keyOf(leg)) ?? null;
}
